import type { ComponentType } from 'react';

import { cn } from '@/lib/utils';

import { ValidationDisplay } from './ValidationDisplay';
import { QUESTION_RENDERERS } from './registry';
import type {
  FormQuestion,
  QuestionMode,
  QuestionRendererProps as RendererProps,
  QuestionValueMap,
} from './types';

interface QuestionRendererProps {
  question: FormQuestion;
  value?: unknown;
  onChange?: (value: unknown) => void;
  onBlur?: () => void;
  sessionToken?: string | null;
  ensureSessionToken?: () => Promise<string | null>;
  projectId?: string;
  formId?: string;
  version?: number;
  error?: string;
  mode: QuestionMode;
  disabled?: boolean;
  className?: string;
  /** Show validation hints even in preview mode */
  showHints?: boolean;
}

export const QuestionRenderer = ({
  question,
  value,
  onChange,
  onBlur,
  sessionToken,
  ensureSessionToken,
  projectId,
  formId,
  version,
  error,
  mode,
  disabled,
  className,
  showHints,
}: QuestionRendererProps) => {
  const Renderer = QUESTION_RENDERERS[question.type] as ComponentType<RendererProps>;

  if (!Renderer) {
    return (
      <div className={cn('rounded-md border border-border/60 bg-muted/20 p-4 text-sm text-muted-foreground', className)}>
        Unsupported question type: <span className="font-medium">{question.type}</span>
      </div>
    );
  }

  const required =
    question.type === "read_only_text" ||
    question.type === "link" ||
    question.type === "rich_text" ||
    question.type === "image" ||
    question.type === "divider" ||
    question.type === "callout"
      ? false
      : question.required || question.validation?.required;
  const title = question.title || 'Untitled question';
  const hideQuestionChrome =
    question.type === "rich_text" ||
    question.type === "image" ||
    question.type === "divider" ||
    question.type === "callout";

  return (
    <div className={cn('space-y-4', className)}>
      {!hideQuestionChrome ? (
        <div className="space-y-1">
          <div className="flex items-baseline gap-2">
            <h3 className="form-heading text-base font-semibold text-foreground">{title}</h3>
            {required ? <span className="text-sm font-semibold text-destructive" aria-hidden="true">*</span> : null}
          </div>
          {question.description ? <p className="text-sm text-muted-foreground">{question.description}</p> : null}
        </div>
      ) : null}
      <Renderer
        question={question}
        value={value as QuestionValueMap[FormQuestion['type']]}
        onChange={onChange as RendererProps['onChange']}
        onBlur={onBlur as RendererProps['onBlur']}
        sessionToken={sessionToken ?? undefined}
        ensureSessionToken={ensureSessionToken}
        projectId={projectId}
        formId={formId}
        version={version}
        error={error}
        mode={mode}
        disabled={disabled}
      />
      <ValidationDisplay question={question} error={error} showHints={showHints ?? mode === 'runtime'} />
    </div>
  );
};
